import { useState } from 'react'

import { type OptimizeTransportLotRequest, type OptimizeTransportLot200Response } from '../ds-model-api'

import type { ErrorResponse } from 'types/types'

export interface OptimizeTransportLotResult {
  status: 'success' | 'error'
  message: string
  systemDate: string | null
  pageNo: number
  systemInfo: any | null
}

const validateRequest = (request: OptimizeTransportLotRequest): string | null => {
  if (!request.logic) return '最適化の方向は必須です。'
  if (typeof request.fixLoadCapacity !== 'number' || request.fixLoadCapacity <= 0) {
    return '固定積載率は正の数で入力してください。'
  }
  if (typeof request.minLoadCapacity !== 'number' || request.minLoadCapacity <= 0) {
    return '最小積載率は正の数で入力してください。'
  }
  if (typeof request.minOrderQuantityOnce !== 'number' || request.minOrderQuantityOnce <= 0) {
    return '最小発注量は正の数で入力してください。'
  }
  if (!request.todayTransportLotList?.length && !request.nextdayTransportLotList?.length) {
    return '輸送ロットリストは必須です。'
  }
  if (!request.todayStockList?.length && !request.nextdayStockList?.length) {
    return '在庫リストは必須です。'
  }
  return null
}

export const useTransportLotOptimize = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [result, setResult] = useState<OptimizeTransportLotResult | undefined>(undefined)
  const [optimizeData, setOptimizeData] = useState<OptimizeTransportLot200Response | undefined>(undefined)

  const optimizeTransportLot = async (request: OptimizeTransportLotRequest, api: any): Promise<OptimizeTransportLot200Response> => {
    setResult(undefined)
    setIsLoading(true)

    // Validate request
    const validationError = validateRequest(request)
    if (validationError) {
      const errorResult: OptimizeTransportLotResult = {
        status: 'error',
        message: validationError,
        systemDate: null,
        pageNo: 0,
        systemInfo: null,
      }
      setResult(errorResult)
      throw new Error(validationError)
    }

    try {
      const response = await api.optimizeTransportLot({
        optimizeTransportLotRequest: request
      })
      
      setOptimizeData(response)
      setResult({
        status: 'success',
        message: '最適化が完了しました。',
        systemDate: null,
        pageNo: 0,
        systemInfo: null,
      })
      return response
    } catch (err) {
      const error = err as ErrorResponse
      let errorMessage = '発注日積載率調整でエラーが発生しました。'
      if (error.response) {
        if (error.response.status === 422) {
          errorMessage = '入力値に誤りがあります。'
        } else if (error.response.status === 500) {
          let serverMessage = ''
          try {
            const clone = error.response.clone();
            const data = await clone.json();
            serverMessage = data?.message;
          } catch (e) {
            serverMessage = '';
          }
          errorMessage = serverMessage || 'サーバーエラーが発生しました。'
        } else {
          errorMessage = error.response?.statusText || `発注日積載率調整でエラーが発生しました。(${error.response.status})`
        }
      }
      const errorResult: OptimizeTransportLotResult = {
        status: 'error',
        message: errorMessage,
        systemDate: null,
        pageNo: 0,
        systemInfo: null,
      }
      setResult(errorResult)
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  return {
    result,
    optimizeData,
    optimizeTransportLot,
    isLoading,
  }
}
